/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import React from "react";

let pageNumber: number = 0;
let pageSize: number = 0;
let totalRecords: number = 0;
let totalPages: number = 0

export const ListControl:React.FC<{ pageNumber: number, 
                                        pageSize: number, 
                                        totalRecords: number, 
                                        totalPages: number, 
                                        pageClick: any,
                                        sizeClick: any
                                        handleFilterByStatusChange: (status: string | null) => void;
                                        activeStatusKey: string | null;
                                        onStatusChange: (key: string | null) => void;
                                    }> = (props) => {
    
    const searchCols: React.ReactElement[] = [];

    const pageNumber: number = props.pageNumber;
    const pageSize: number = props.pageSize;
    const totalRecords: number = props.totalRecords;
    const totalPages: number = props.totalPages;

    const spanStyle = {
        textWrap: 'nowrap'
    };

    const handleStatusToggle = (status: string) => {
        const newStatus = props.activeStatusKey === status ? null : status;
        props.handleFilterByStatusChange(newStatus);
        props.onStatusChange(newStatus);
    };

    return(
        <>
         <div>
           <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 flex-wrap">
            {/* LEFT: Total Records + Current Page */}
            <div className="d-flex flex-row align-items-center gap-3 mt-2">
                <span className="card-subtitle text-muted" style={{ lineHeight: '1.8' }}>
                    Total Records: [{totalRecords}]
                </span>
                <span className="card-subtitle text-muted" style={{ lineHeight: '1.8' }}>
                    Current Page: [{pageNumber + 1} of {totalPages}]
                </span>
            </div>

            {/* RIGHT SIDE: Filter, Page Size, Pagination */}
            <div className="d-flex flex-wrap flex-row align-items-center gap-3 justify-content-end">
                {/* Filter Dropdown */}
                <div className="dropdown d-flex align-items-center" style={{ position: 'relative', minWidth: '260px' }}>
                <button className="btn btn-outline-secondary dropdown-toggle w-100 sa-dropdown-button m-0" type="button" id="checkboxDropdown" data-bs-toggle="dropdown" data-bs-offset="0,25" aria-expanded="false"
                    style={{ paddingTop: '0.5rem', paddingBottom: '0.4rem', lineHeight: 1.5}}
                >
                    Filter By Integration Status
                </button>
                <ul className="dropdown-menu p-3 mt-5" aria-labelledby="checkboxDropdown" style={{minWidth: '200px', zIndex: 9999}}>
                    {['ERROR', 'CANCELLED', 'PROCESSING', 'CLOSED'].map((statusKey) => (
                    <li key={statusKey} className="form-check" onChange={() => handleStatusToggle(statusKey)}>
                        <input
                        className="form-check-input"
                        type="checkbox"
                        id={`status-${statusKey}`}
                        checked={props.activeStatusKey === statusKey}
                        style={{
                            border: '1px solid #343a40',
                            boxShadow: props.activeStatusKey === statusKey ? '0 0 0 2px rgba(52, 58, 64, 0.5)' : 'none',
                            accentColor: '#343a40',
                            cursor: 'pointer',
                            backgroundColor: props.activeStatusKey === statusKey ? '#343a40' : 'transparent',
                        }}
                        />
                        <label className="form-check-label" htmlFor={`status-${statusKey}`} style={{ cursor: 'pointer', paddingTop: '4px' }}>
                        Integration Status [{statusKey}]
                        </label>
                    </li>
                    ))}
                </ul>
                </div>

                {/* Page Size Selector */}
                <nav>
                <ul className="pagination m-0">
                    <li className="page-item">
                    <select
                        onChange={props.sizeClick}
                        className="page-link"
                        style={{ width: '100px', textAlign: 'center', padding: '10px' }}
                    >
                        <option defaultChecked value={pageSize}>
                        {pageSize}
                        </option>
                        {[25, 50, 75, 100, 150, 200, 250, 500, 750, 1000].map((size) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                        ))}
                    </select>
                    </li>
                </ul>
                </nav>
                {/* // Pagination Control */}
                <nav aria-label="">
                    <ul className="pagination m-0">
                    <li className={`page-item ${pageNumber === 0 ? 'disabled' : ''}`}>
                        <button 
                        className="page-link d-flex align-items-center justify-content-center gap-1"
                        onClick={() => props.pageClick(0)}
                        disabled={pageNumber === 0}
                        style={{ height: '38px', marginTop: '1px' }}
                        >
                        <ChevronsLeft size={18} />
                        </button>
                    </li>

                    <li className={`page-item ${pageNumber <= 0 ? 'disabled' : ''}`}>
                        <button 
                        className="page-link d-flex align-items-center justify-content-center"
                        onClick={() => props.pageClick(pageNumber - 1)}
                        disabled={pageNumber <= 0}
                        style={{ height: '38px', marginTop: '1px' }}
                        >
                        <ChevronLeft size={18} />
                        </button>
                    </li>

                    <li className="page-item">
                        <span
                        className="page-link d-flex align-items-center justify-content-center"
                        style={{ height: '38px', marginTop: '1px', padding: '0 14px' }}
                        >
                            Page {pageNumber + 1} of {totalPages}
                        </span>
                    </li>

                    <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''}`}>
                        <button 
                        className="page-link d-flex align-items-center justify-content-center"
                        onClick={() => props.pageClick(pageNumber + 1)}
                        disabled={pageNumber >= totalPages - 1}
                        style={{ height: '38px', marginTop: '1px' }}
                        >
                        <ChevronRight size={18} />
                        </button>
                    </li>

                    <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''}`}>
                        <button 
                        className="page-link d-flex align-items-center justify-content-center gap-1"
                        onClick={() => props.pageClick(totalPages - 1)}
                        disabled={pageNumber >= totalPages - 1}
                        style={{ height: '38px', marginTop: '1px' }}
                        >
                        <ChevronsRight size={18} />
                        </button>
                    </li>
                    </ul>
                </nav>
            </div>
          </div>
        </div>
        </>
    );
}